import {Component, Input, OnInit} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {Subscription} from 'rxjs/Subscription';
import {Location} from '@angular/common';
import {CaregiverService, ConfirmationService, ModalService, PatientService, UserService} from '../../services';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {take} from "rxjs/operators";

interface User {
  id: number;
  name: string;
  avatar: string;
  address: string;
  device: string;
  imei: string;
  batteryLevel?: number;
  first_name: string;
  last_name: string;
}

interface Tab {
  id: string;
  label: string;
  icon?: string;
  customIconClass?: string;
  event: string;
}

/**
 * Lista opiekunów szuka po stronie serwera, gdzie ogonki znosi kolacja bazy; tutaj
 * filtrujemy w przeglądarce, więc normalizacja musi być własna. „ł" nie ma rozkładu
 * kanonicznego, więc samo NFD go nie zdejmie.
 */
function normalizeForSearch(value: string): string {
  return (value || '')
    .toLowerCase()
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function matchesTerm(user: User, term: string): boolean {
  const fullName = `${normalizeForSearch(user.first_name)} ${normalizeForSearch(user.last_name)}`;

  return fullName.includes(term)
    || (user.imei || '').toLowerCase().includes(term);
}

@Component({
  selector: 'app-pop-up-assign-users',
  templateUrl: './pop-up-assign-users.component.html',
  styleUrls: ['./pop-up-assign-users.component.scss']
})
export class PopUpAssignUsersComponent implements OnInit  {

  public searchKeys = ['first_name', 'last_name', 'imei'];
  public subscription: Subscription;
  public caregiver;
  public isSubmitting = false;
  public pendingUser = null;
  public ifEverSearching = false;

  public users = [];
  public usersDetach = [];
  public isLoading: boolean;
  public usersResults: Array<any>;
  public usersDetachResults: Array<any>;
  public searchTerm: string = '';

  @Input() id!: number;

  tabs: Tab[] = [];

  activeTab = 'assign-panel';

  constructor(
    private location: Location,
    private patientService: PatientService,
    private caregiverService: CaregiverService,
    private userService: UserService,
    private modalService: ModalService,
    private confirmation: ConfirmationService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    public activeModal: NgbActiveModal
  ) { }

  ngOnInit() {
    this.tabs = [
      {
        id: 'assign-panel',
        label: this.translateService.instant('COMMON.AVAILABLE_USERS'),
        icon: '/assets/images/UserPlus.svg',
        event: 'EVENTS.SHARED.COMPONENTS.POP-UP-ASSIGN-USERS.TAB.ASSIGN-PANEL',
      },
      {
        id: 'detach-panel',
        label: this.translateService.instant('COMMON.ASSIGNED_USERS'),
        icon: '/assets/images/UsersFour.svg',
        event: 'EVENTS.SHARED.COMPONENTS.POP-UP-ASSIGN-USERS.TAB.DETACH-PANEL',
      }
    ];
    this.activeTab = this.tabs[0].id;
    this.getPatientsToAssign();
  }

  setActive(tabId: string) {
    if (tabId === this.activeTab) return;

    this.searchTerm = '';
    this.ifEverSearching = false;
    this.activeTab = tabId;

    if (tabId === 'assign-panel') {
      this.getPatientsToAssign();
    } else if (tabId === 'detach-panel') {
      this.getPatientsToDetach();
    }
  }

  isActive(tabId: string) {
    return this.activeTab === tabId;
  }

  onSearchChange(searchValue: string) {
    // zachowujemy tekst dokładnie tak, jak wpisał użytkownik (ngModel wpisuje go z powrotem do inputa);
    // normalizacja wielkości liter odbywa się dopiero przy filtrowaniu
    this.searchTerm = searchValue;
    this.filterUsers();
  }

  filterUsers() {
    const term = normalizeForSearch(this.searchTerm);

    if (!term) {
      this.usersResults = this.users;

      this.ifEverSearching = false;
      return;
    }

    this.ifEverSearching = true;

    if (this.activeTab == 'assign-panel') {
      this.usersResults = this.users.filter(user => matchesTerm(user, term));
    } else {
      this.usersDetachResults = this.usersDetach.filter(user => matchesTerm(user, term));
    }
  }

  assignUser(user) {
    this.isSubmitting = true;
    this.pendingUser = user;

    const data = { caregiver: this.caregiver.id, patientImei: user && user.imei ? user.imei : null, patientUserId: user ? user.id : null };

    this.caregiverService.assignUser(data)
      .subscribe(() => {

        const userIndex = this.users.findIndex(i => (i.id && user.id) ? i.id === user.id : i.imei === user.imei);
        this.users.splice(userIndex, 1);
        this.filterUsers();
        this.confirmation.show('BACKEND.ASSIGNED-USER', undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-ASSIGN-USERS.POPUP.BACKEND-ASSIGNED-USER');
        this.isSubmitting = false;
        this.pendingUser = null;

        if (this.ifEverSearching)
          this.refresh();
      }, () => {
        this.isSubmitting = false;
        this.pendingUser = null;
        this.modalService.open(this.translateService.instant('BACKEND.COULDNT-ASSIGN'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-ASSIGN-USERS.POPUP.BACKEND-COULDNT-ASSIGN');
      });
  }

  public detachUser(user): void {

    const credentials = {
      caregiver: this.caregiver.id,
      patientImei: user && user.imei ? user.imei : null,
      patientUserId: user ? user.id : null,
    };

    this.modalService.openAccept(this.translateService.instant('BACKEND.ARE-YOU-SURE'), 'EVENTS.SHARED.COMPONENTS.POP-UP-ASSIGN-USERS.POPUP.BACKEND-ARE-YOU-SURE')
      .pipe(take(1))
      .subscribe(
        data => {
          if (data.ok === true) {

            this.caregiverService.deleteUser(credentials)
              .subscribe(res => {
                this.getPatientsToDetach();
              });

          }
        },
        err => this.modalService.open(this.translateService.instant('BACKEND.COULDNT-DELETE'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-ASSIGN-USERS.POPUP.BACKEND-COULDNT-DELETE')
      );

  }

  assignAllUser() {
    this.isSubmitting = true;
    let userMe = this.userService.getCurrentUser();
    let data = { idCaregiver: this.caregiver.id, idManager: userMe.user_id };

    this.caregiverService.assignAllUser(data)
      .subscribe(response => {
        this.isSubmitting = false;
        if (response == 'no_records') {
          this.modalService.open(this.translateService.instant('BACKEND.NO-USERS-FOUND'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-ASSIGN-USERS.POPUP.BACKEND-NO-USERS-FOUND');
          return false;
        }
        this.confirmation.show('BACKEND.ASSIGNED-USERS', undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-ASSIGN-USERS.POPUP.BACKEND-ASSIGNED-USERS');
        this.activeModal.close();
        this.router.navigateByUrl("/caregivers/caregiver/" + this.caregiver.id);
      }, error => {
        this.isSubmitting = false;
      });
  }

  presentedResults(event) {
    this.ifEverSearching = true;
    this.usersResults = event;
  }

  goBack() {
    this.location.back();
  }

  ngOnDestroy() {
    if (this.subscription) { this.subscription.unsubscribe(); }
  }

  getPatientsToAssign() {
    this.subscription = this.route.params.subscribe(params => {

      this.caregiverService.getCaregiver(this.id)
        .subscribe(
          res => {
            this.caregiver = res.data;
          },
          // () => this.modalService.open(this.translateService.instant('BACKEND.COULDNT-GET-DATA'))
        );

      this.patientService.getPatientsToAssign(this.id).subscribe(
        data => {
          this.users = (data || []).filter(user => user.imei);
          this.usersResults = this.users;
        },
        // () => this.modalService.open(this.translateService.instant('BACKEND.COULDNT-GET-DATA'))
      );

    });
  }

  getPatientsToDetach() {
    this.subscription = this.route.params.subscribe(params => {

      this.caregiverService.getCaregiver(this.id)
        .subscribe(
          res => {
            this.caregiver = res.data;
          },
          // err => this.modalService.open(this.translateService.instant('BACKEND.COULDNT-GET-DATA'))
        );

      this.patientService.getPatientsToDetach(this.id).subscribe(
        data => {
          this.usersDetach = (data || []).filter(user => user.imei);
          this.usersDetachResults = this.usersDetach;
        },
        // err => this.modalService.open(this.translateService.instant('BACKEND.COULDNT-GET-DATA'))
      );

    });
  }

  refresh() {
    window.location.reload();
  }

  paginateUsers(data) {
    this.usersResults = data;
  }

  trimText(text: string, maxLength: number = 30): string {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

}
